/**
 * Spec 219 — phone number helpers shared by the web client.
 *
 * Storage contract (cross-stack):
 *   Phones persist as E.164 (`+34612345678`, no spaces) so a value
 *   captured on web renders identically on mobile and vice-versa.
 *   The display formatting is per-country and lives in the UI
 *   layer — the data layer never sees pretty strings.
 *
 * Lazy-load `libphonenumber-js/min`:
 *   `min` is the smallest variant (~75 KB metadata) and covers the
 *   E.164 validate + parse + format use cases this app needs. We
 *   gate it behind a dynamic import so the WorkList route + Pair
 *   route never pay the metadata cost — only screens that mount a
 *   PhoneInput trigger the load.
 *
 * Default country:
 *   ES (+34) — the user base lives there. NO geolocation IP lookup
 *   (privacy: the relay must stay blind, the web must not phone
 *   home for IP intel). The user can change the default per session;
 *   we persist it via `getDefaultCountry` / `setDefaultCountry`
 *   below in localStorage so a return visit feels right.
 */

export type CountryIso2 = string; // 'ES', 'US', 'FR' etc.

const DEFAULT_COUNTRY: CountryIso2 = 'ES';
const STORAGE_KEY = 'cuadrilla.phone.defaultCountry';

let modulePromise: Promise<typeof import('libphonenumber-js/min')> | null = null;

/** Cached dynamic import of `libphonenumber-js/min`. The first call
 *  triggers the network/cache fetch; subsequent callers reuse the
 *  promise. */
function loadLib(): Promise<typeof import('libphonenumber-js/min')> {
  if (!modulePromise) {
    modulePromise = import('libphonenumber-js/min');
  }
  return modulePromise;
}

/**
 * Convert a free-form phone string into E.164. Returns null when
 * the input doesn't parse to a valid number under the supplied
 * default country.
 *
 * Accepts:
 *   - '+34612345678'        → '+34612345678'
 *   - '+34 612 345 678'     → '+34612345678'
 *   - '612 345 678' (default ES) → '+34612345678'
 *   - '(555) 123 4567' (default US) → '+15551234567'
 *
 * Returns null for blank input, gibberish, or a number that fails
 * the libphonenumber-js validity heuristics for the resolved
 * country (e.g. wrong length).
 */
export async function normalizeE164(
  input: string,
  defaultCountry: CountryIso2 = DEFAULT_COUNTRY,
): Promise<string | null> {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const lib = await loadLib();
  // `parsePhoneNumberFromString` accepts undefined when the input
  // already starts with '+', and a country hint otherwise. Pass the
  // hint always — it's harmless on E.164 input.
  // Cast: the lib's CountryCode type is a giant union; we accept
  // any ISO-2 string at the boundary and let the lib validate.
  const parsed = lib.parsePhoneNumberFromString(
    trimmed,
    defaultCountry as Parameters<typeof lib.parsePhoneNumberFromString>[1],
  );
  if (!parsed || !parsed.isValid()) return null;
  return parsed.format('E.164');
}

/**
 * Reformat an E.164 string for display. Returns the input verbatim
 * if it doesn't parse — never throws — so the caller can fall back
 * to legacy data without a try/catch.
 *
 * Format used: the country's national-international convention —
 *   ES: '+34 612 34 56 78'
 *   US: '+1 555 123 4567'
 *   FR: '+33 6 12 34 56 78'
 */
export async function formatPhoneForDisplay(e164OrInput: string): Promise<string> {
  const trimmed = e164OrInput.trim();
  if (!trimmed) return '';
  try {
    const lib = await loadLib();
    const parsed = lib.parsePhoneNumberFromString(trimmed);
    if (!parsed) return trimmed;
    return parsed.formatInternational();
  } catch {
    return trimmed;
  }
}

/**
 * Pull the country ISO-2 out of an E.164 string. Useful when an
 * already-saved phone hits a fresh PhoneInput mount: the picker
 * should reflect the saved country (not the session default).
 *
 * Returns null when the input doesn't parse — caller falls back to
 * the session default.
 */
export async function parseCountryFromE164(e164: string): Promise<CountryIso2 | null> {
  const trimmed = e164.trim();
  if (!trimmed) return null;
  try {
    const lib = await loadLib();
    const parsed = lib.parsePhoneNumberFromString(trimmed);
    return parsed?.country ?? null;
  } catch {
    return null;
  }
}

/** Read the per-session default country preference. */
export function getDefaultCountry(): CountryIso2 {
  try {
    const stored = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (stored && /^[A-Z]{2}$/.test(stored)) return stored;
  } catch {
    // localStorage unavailable (private mode, SSR fallback) — fall through.
  }
  return DEFAULT_COUNTRY;
}

/** Persist the per-session default country preference. NEVER syncs
 *  to the team — phone-input UX is a personal preference. */
export function setDefaultCountry(country: CountryIso2): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, country);
  } catch {
    // Same as getDefaultCountry — silent no-op when storage is denied.
  }
}

/**
 * Sync version of E.164 validation that returns boolean only. The
 * full lib still loads lazily — the validators surface in
 * `web/src/utils/validators.ts` already exposes a sync regex check,
 * which we keep for the optimistic-rendering path. The async helper
 * above is the source of truth at save time.
 */
export async function isValidE164(input: string, defaultCountry: CountryIso2 = DEFAULT_COUNTRY): Promise<boolean> {
  return (await normalizeE164(input, defaultCountry)) !== null;
}
